import { ApplicationConfig, APP_INITIALIZER, provideAppInitializer, inject } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { APP_PRIMARY_THEME } from './theme/theme-presets';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { companyScopeInterceptor } from './core/interceptors/company-scope.interceptor';
import { mockCriteriaApiInterceptor } from './core/interceptors/mock-criteria-api.interceptor';
import { mockTemplateApiInterceptor } from './core/interceptors/mock-template-api.interceptor';
import { mockAttendanceApiInterceptor } from './core/interceptors/mock-attendance-api.interceptor';
import { mockEmployeeRegistrationApiInterceptor } from './core/interceptors/mock-employee-registration-api.interceptor';
import { mockBenefitApiInterceptor } from './core/interceptors/mock-benefit-api.interceptor';
import { userIdInterceptor } from './core/interceptors/user-id.interceptor';
import { mockRecruitmentApiInterceptor } from './core/interceptors/mock-recruitment-api.interceptor';
import { MetadataService } from './core/services/metadata.service';
import { NavigationService } from './core/services/navigation.service';
import { environment } from '../environments/environment';

// Field registry
import { FieldRegistryService } from './shared/services/field-registry.service';
import { TextFieldComponent } from './shared/ui/form-fields/text-field.component';
import { NumberFieldComponent } from './shared/ui/form-fields/number-field.component';
import { TextareaFieldComponent } from './shared/ui/form-fields/textarea-field.component';
import { SelectFieldComponent } from './shared/ui/form-fields/select-field.component';
import { MultiselectFieldComponent } from './shared/ui/form-fields/multiselect-field.component';
import { TreeselectFieldComponent } from './shared/ui/form-fields/treeselect-field.component';
import { TemplateFieldComponent } from './shared/ui/form-fields/template-field.component';
import { DateFieldComponent } from './shared/ui/form-fields/date-field.component';
import { DaterangeFieldComponent } from './shared/ui/form-fields/daterange-field.component';
import { ToggleFieldComponent } from './shared/ui/form-fields/toggle-field.component';
import { RadioGroupFieldComponent } from './shared/ui/form-fields/radio-group-field.component';
import { CheckboxGroupFieldComponent } from './shared/ui/form-fields/checkbox-group-field.component';
import { PhotoUploadFieldComponent } from './shared/ui/form-fields/photo-upload-field.component';
import { TableEditorFieldComponent } from './shared/ui/form-fields/table-editor-field.component';
import { AccordionArrayFieldComponent } from './shared/ui/form-fields/accordion-array-field.component';
import { NumberPercentFieldComponent } from './shared/ui/form-fields/number-percent-field.component';
import { CurrencyFieldComponent } from './shared/ui/form-fields/currency-field.component';
import { FileUploadFieldComponent } from './shared/ui/form-fields/file-upload-field.component';
import { TimeFieldComponent } from './shared/ui/form-fields/time-field.component';
import { DatetimeFieldComponent } from './shared/ui/form-fields/datetime-field.component';
import { LabelFieldComponent } from './shared/ui/form-fields/label-field.component';

import {
    provideKeycloak, withAutoRefreshToken,
    AutoRefreshTokenService,
    UserActivityService,
    KeycloakService
} from 'keycloak-angular';



function registerFieldComponents(registry: FieldRegistryService): () => void {
    return () => {
        registry.register('text', TextFieldComponent);
        registry.register('email', TextFieldComponent);
        registry.register('password', TextFieldComponent);
        registry.register('number', NumberFieldComponent);
        registry.register('textarea', TextareaFieldComponent);
        registry.register('select', SelectFieldComponent);
        registry.register('multiselect', MultiselectFieldComponent);
        registry.register('multi_select', MultiselectFieldComponent);
        registry.register('treeselect', TreeselectFieldComponent);
        registry.register('template', TemplateFieldComponent);
        registry.register('date', DateFieldComponent);
        registry.register('daterange', DaterangeFieldComponent);
        registry.register('toggle', ToggleFieldComponent);
        registry.register('radio-group', RadioGroupFieldComponent);
        registry.register('checkbox-group', CheckboxGroupFieldComponent);
        registry.register('photo_upload', PhotoUploadFieldComponent);
        registry.register('table-editor', TableEditorFieldComponent);
        registry.register('accordion-array', AccordionArrayFieldComponent);
        registry.register('number_percent', NumberPercentFieldComponent);
        registry.register('currency', CurrencyFieldComponent);
        registry.register('file_upload', FileUploadFieldComponent);
        registry.register('file', FileUploadFieldComponent);
        registry.register('time', TimeFieldComponent);
        registry.register('datetime', DatetimeFieldComponent);
        registry.register('label', LabelFieldComponent);
    };
}


export const appConfig: ApplicationConfig = {
    providers: [
        provideHttpClient(withFetch(), withInterceptors([
            ...(environment.authMode === 'mock' ? [mockCriteriaApiInterceptor, mockTemplateApiInterceptor, mockEmployeeRegistrationApiInterceptor, mockAttendanceApiInterceptor, mockRecruitmentApiInterceptor] : []), mockBenefitApiInterceptor,
            authInterceptor,
            companyScopeInterceptor,
            userIdInterceptor,
            errorInterceptor
        ])),
        provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: APP_PRIMARY_THEME,
                options: {
                    darkModeSelector: '.dark'
                }
            }
        }),
        MessageService,
        ConfirmationService,
        DialogService,
        {
            provide: APP_INITIALIZER,
            useFactory: (meta: MetadataService) => () => meta.loadMetadata(),
            deps: [MetadataService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (nav: NavigationService) => () => nav.loadMenu(),
            deps: [NavigationService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: registerFieldComponents,
            deps: [FieldRegistryService],
            multi: true
        },
        provideKeycloak({
            config: {
                url: environment.authUrl,
                realm: environment.realm,
                clientId: environment.client_id
            },
            initOptions: {
                onLoad: 'check-sso',
                silentCheckSsoFallback: false,
                silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
                checkLoginIframe: false
            },
            features: [
                withAutoRefreshToken()
            ],
            providers: [
                AutoRefreshTokenService,
                UserActivityService
            ]
        }),
        KeycloakService
    ]
};

provideAppInitializer(() => {
    const keycloakService = inject(KeycloakService);
    return keycloakService.init(); // Angular waits for this promise before rendering anything
});